import { css, useTheme } from "@emotion/react";
import clsx from "clsx";
import {
  Children,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Button from "./Button.jsx";
import useComponentWidth from "./useComponentWidth.js";
import useForkRef from "./useForkRef";
import usePrevious from "./usePrevious.js";
import useThemeProps from "./useThemeProps.jsx";

const InlineListItem = forwardRef(function InlineListItem(
  { children, ...restProps },
  inRef,
) {
  return (
    <li ref={inRef} css={css``} {...restProps}>
      {children}
    </li>
  );
});

const InlineListOverflow = forwardRef(function InlineListOverflow(
  props,
  inRef,
) {
  const { children, isExpanded, onToggle, ...restProps } = props;
  return (
    <div
      ref={inRef}
      css={css`
        position: relative;
      `}
      {...restProps}
    >
      <Button onClick={onToggle}>More ↓</Button>
      {isExpanded && (
        <ul
          css={css`
            position: absolute;
            top: 0;
          `}
        >
          {children}
        </ul>
      )}
    </div>
  );
});

export default forwardRef(function InlineList(props, inRef) {
  let {
    as: Element = "div",
    items,
    renderItem,
    children,
    spacing = 2,
    align = "start",
    alignItems = "start",
    overflow = "visible",
    components = {},
    minCount = 0,
    stretchOnMaxWidth = 0,
    className,
    ...restProps
  } = useThemeProps({
    props,
    name: "InlineList",
  });
  const { Overflow = InlineListOverflow } = components;
  const theme = useTheme();
  const gap = theme.getLength(spacing);
  const [width, widthRef] = useComponentWidth();
  const [isExpanded, setIsExpanded] = useState(false);

  const hideItems = overflow === "collapse" || overflow === "hidden";

  renderItem = useMemo(
    () => renderItem || (items ? ({ render }) => render() : (item) => item),
    [renderItem, !items],
  );

  if (!items) {
    items = Children.toArray(children).filter((child) => child);
  }

  const listElementRef = useRef(null);

  const ref = useForkRef([widthRef, inRef]);
  const listRef = useForkRef([listElementRef]);

  const stretch = width <= theme.getLengthPx(stretchOnMaxWidth);

  children = Children.toArray(children);

  let [visibleItems, setVisibleItems] = useState(
    hideItems ? 1 : children.length,
  );
  let previousWidth = usePrevious(width);
  let previousVisibleItems = usePrevious(visibleItems);
  let previousChildren = usePrevious(children.length);
  let didChildrenChange = children.length !== previousChildren;
  let didAdd = visibleItems > previousVisibleItems;
  let didGrow = width > previousWidth;

  useEffect(() => {
    let isOverflowing =
      listElementRef.current &&
      listElementRef.current.scrollWidth > listElementRef.current.offsetWidth;
    if (!hideItems) {
      // console.log("did grow");
      setVisibleItems(children.length);
    } else if (didGrow) {
      // console.log("did grow");
      setVisibleItems(Math.min(visibleItems + 1, children.length));
    } else if (didAdd && !isOverflowing) {
      // console.log("did add and not overflowing");
      setVisibleItems(Math.min(visibleItems + 1, children.length));
    } else if (didChildrenChange && isOverflowing) {
      // console.log("did children change and overflowing");
      setVisibleItems(Math.max(visibleItems - 1, minCount));
    } else if (didChildrenChange) {
      // console.log("did children change and not overflowing");
      setVisibleItems(Math.min(visibleItems + 1, children.length));
    } else if (isOverflowing && visibleItems > minCount) {
      // console.log("is overflowing and visible items > 1");
      setVisibleItems(Math.max(visibleItems - 1, minCount));
    }
  });

  const stateClassName = clsx({
    "wsui-is-content-visible": overflow !== "hidden" || visibleItems > 0,
    "wsui-is-content-hidden": overflow === "hidden" && visibleItems === 0,
  });

  return (
    <Element
      ref={ref}
      css={css`
        display: flex;
        flex-direction: row;
        justify-content: ${align};
        gap: ${visibleItems > 0 ? gap : 0};
        padding: 0;
        max-width: 100%;
        overflow: visible;
        align-items: ${alignItems};
      `}
      className={clsx("wsui-inline-list", stateClassName, className)}
      {...restProps}
    >
      <ul
        ref={listRef}
        css={css`
          display: flex;
          flex-direction: row;
          flex-wrap: ${overflow === "visible" ? "wrap" : "nowrap"};
          gap: ${gap};
          justify-content: ${overflow === "collapse" ? "start" : align};
          justify-items: ${overflow === "visible" && stretch
            ? "stretch"
            : "auto"};
          align-items: ${alignItems};
          list-style: none;
          padding: 0;
          margin: 0;
          min-width: 0;
          max-width: max-content;
          overflow: visible;
          flex: 1 1 0;
        `}
      >
        {Object.entries(children)
          .slice(0, visibleItems)
          .map(([index, child]) => (
            <InlineListItem
              // data-inline-list-item={index}
              key={index}
              // ref={itemRef}
              css={css`
                display: ${stretch ? "flex" : "inherit"};
                width: ${stretch ? "100%" : "auto"};
                flex-shrink: 0;
                /* position: ${index < visibleItems ? "static" : "absolute"};
                visibility: ${index < visibleItems ? "visible" : "collapse"}; */
              `}
            >
              {child}
            </InlineListItem>
          ))}
      </ul>
      {visibleItems < children.length && overflow === "collapse" && (
        <Overflow
          css={css`
            flex: 0 0 auto;
          `}
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded((expanded) => !expanded)}
        >
          {Object.entries(children)
            .slice(visibleItems)
            .map(([index, child]) => (
              <InlineListItem
                // data-inline-list-item={index}
                key={index}
                // ref={itemRef}
                // css={css`
                //   ${intersectingItems.includes(index) ? "" : "visibility: hidden;"}
                // `}
              >
                {child}
              </InlineListItem>
            ))}
        </Overflow>
      )}
    </Element>
  );
});
